import React from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { Dialog, DialogBackdrop, DialogPanel, DialogProps, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '6.5xl': 'sm:max-w-[76rem]',
  '7xl': 'sm:max-w-7xl'
};

export const Slideout = (
  props: React.PropsWithChildren<{
    size?: keyof typeof sizes;
    showing?: boolean;
    title: string;
    onClose: () => void;
  }>
) => {
  return (
    <Dialog open={!!props.showing} onClose={props.onClose} className="relative z-40">
      <DialogBackdrop
        transition
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-neutral-950/50 px-2 py-2 transition duration-200 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-neutral-950/75"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              className={clsx(
                'pointer-events-auto w-screen max-w-full transform transition-all duration-200 data-[closed]:translate-x-1/2 data-[closed]:opacity-0',
                sizes[props.size || 'lg']
              )}
            >
              <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-neutral-900 py-6 shadow-xl">
                <div className="px-4 sm:px-6">
                  <div className="flex items-start justify-between">
                    <DialogTitle className="text-base font-medium font-sans m-0 text-neutral-900 dark:text-white">
                      {props.title}
                    </DialogTitle>
                    <div className="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={props.onClose}
                        className="relative rounded-md bg-white text-neutral-400 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-neutral-900 dark:text-neutral-500 dark:hover:text-neutral-400"
                      >
                        <span className="absolute -inset-2.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="relative mt-6 flex-1 px-4 sm:px-6">{props.children}</div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export function MountableSlideout({
  size = 'lg',
  className,
  children,
  title,
  actions,
  ...props
}: {
  size?: keyof typeof sizes;
  className?: string;
  title: string | React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
} & Omit<DialogProps, 'className' | 'title'>) {
  return (
    <Dialog static open {...props} className="relative z-50">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1, ease: 'easeInOut' }}
        className="fixed inset-0 flex w-screen justify-center overflow-y-auto bg-neutral-950/50 px-2 py-2 transition duration-200 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16 dark:bg-neutral-950/75"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel as={React.Fragment}>
              <motion.div
                initial={{ opacity: 0, translateX: '50%' }}
                animate={{ opacity: 1, translateX: '0' }}
                exit={{ opacity: 0, translateX: '50%' }}
                transition={{ duration: 0.15, ease: 'easeInOut' }}
                className={clsx(className, 'pointer-events-auto w-screen max-w-full', sizes[size])}
              >
                <div className="flex h-full flex-col overflow-y-scroll bg-white dark:bg-neutral-900 py-6 shadow-xl">
                  <div className="px-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <DialogTitle className="text-base font-medium font-sans m-0 text-neutral-900 dark:text-white">
                        {title}
                      </DialogTitle>
                      <div className="ml-3 flex items-center gap-3">
                        {actions}
                        <button
                          type="button"
                          onClick={() => props.onClose(false)}
                          className="relative rounded-md bg-white text-neutral-400 hover:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-neutral-900 dark:text-neutral-500 dark:hover:text-neutral-400"
                        >
                          <span className="absolute -inset-2.5" />
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="relative mt-6 flex flex-1 min-h-0 min-w-0 flex-col px-4 sm:px-6">
                    {children}
                  </div>
                </div>
              </motion.div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
